import { z } from 'zod';

import { DCCatalogStatus } from '../types/DCCatalogStatus';
import { communication, compliance, controller, metadata } from '../validators/component-sections';
import { environmental } from '../validators/component-sections/environmental';
import { files } from '../validators/component-sections/files';
import { COMPONENT_IMAGE_TYPE_OPTIONS, images } from '../validators/component-sections/images';
import { mechanical } from '../validators/component-sections/mechanical';
import { performance } from '../validators/component-sections/performance';
import { ManufacturerPayloadSchema, ManufacturerSchema } from '../validators/Manufacturer';
import { ComponentType } from './fields/ComponentType';
import { RegionAvailability } from './fields/RegionAvailability';
import { Website } from './fields/Website';

export enum ComponentVisibility {
    PRIVATE = 'private',
    PUBLIC = 'public',
}

export enum ComponentApplication {
    DC_MICROGRID = 'dc-microgrid',
}

export const ComponentApplicationOptions: {
    value: ComponentApplication;
    label: string;
}[] = [
    {
        value: ComponentApplication.DC_MICROGRID,
        label: 'DC Microgrid',
    },
];

export enum ComponentLaunchStatus {
    RELEASE_DATE = 'releaseDate',
    QUARTER = 'quarter',
    COMING_SOON = 'comingSoon',
}

export const ComponentLaunchStatusOptions: {
    value: ComponentLaunchStatus;
    label: string;
}[] = [
    {
        value: ComponentLaunchStatus.RELEASE_DATE,
        label: 'Known release date',
    },
    {
        value: ComponentLaunchStatus.QUARTER,
        label: 'Release quarter',
    },
    {
        value: ComponentLaunchStatus.COMING_SOON,
        label: 'Coming soon, no date yet',
    },
];

export const RELEASE_QUARTER_PATTERN = /^\d{4}-Q[1-4]$/;

const ComponentBase = z.object({
    type: ComponentType.validator,

    name: z.string(),
    description: z.string().default(''),

    manufacturer: z.union([ManufacturerSchema, ManufacturerSchema.shape.id]).nullable().default(null),
    teamManufacturer: z.string().nullable().default(null),
    distributors: z.array(z.string()).default([]),
    distributorsDetails: z
        .array(
            z.object({
                distributor: z.string(),
                price: z.number().optional(),
            }),
        )
        .default([]),
    productIdentifier: z.string().default(''),
    productSeries: z.string().default(''),
    msrp: z.number().optional().nullable().default(null),
    leadTime: z.number().optional().nullable().default(null),
    lifecycle: z
        .object({
            launchStatus: z.nativeEnum(ComponentLaunchStatus).default(ComponentLaunchStatus.RELEASE_DATE),
            release: z.coerce.date().nullable().default(null),
            releaseQuarter: z.string().regex(RELEASE_QUARTER_PATTERN).nullable().default(null),
            endOfLife: z.coerce.date().nullable().default(null),
        })
        .default({}),
    website: Website.validator,
    regionAvailability: RegionAvailability.validator.default([]),

    compliance,
    application: z.nativeEnum(ComponentApplication).array().optional().default([]),

    communication,
    controller,
    environmental,
    files,
    images,
    mechanical,
    performance,
    questions: z
        .object({
            question: z.string().default(''),
            answer: z.string().default(''),
        })
        .array()
        .default([]),
    standards: z.array(z.string()).default([]),
    archivedAt: z.coerce.date().optional().nullable().default(null),
    deletedAt: z.coerce.date().optional().nullable().default(null),
    visibility: z.nativeEnum(ComponentVisibility).default(ComponentVisibility.PRIVATE),
    reviewed: z.boolean().default(false),
    publicSpecifications: z.boolean().optional(),
    publishedBy: z.string().nullable().default(null),
    publishedAt: z.coerce.date().optional().nullable().default(null),
    metadata,

    compatibleWith: z.array(z.string()).default([]).nullable(),
    compatibleWithCrossReference: z.array(z.string()).default([]).nullable(),
    compatibleWithPlaceholders: z
        .array(
            z.object({
                manufacturer: z.string(),
                productIdentifier: z.string(),
                componentType: ComponentType.validator.optional(),
            }),
        )
        .default([]),

    specificationsSummary: z.string().default(''),

    completeness: z.number().optional(),
    viewedCount: z.number().optional(),
});

export const ComponentValidator = ComponentBase.extend({
    id: z.string(),

    createdBy: z.string(),
    updatedBy: z.string().nullable().default(null),

    team: z.string().optional().default(''),

    catalogs: z
        .object({
            dc: z
                .object({
                    status: z.nativeEnum(DCCatalogStatus).optional(),
                    flaggedAt: z.string().nullable().default(null).optional(),
                    reviewedAt: z.string().nullable().default(null).optional(),
                    reviewedBy: z.string().nullable().default(null).optional(),
                })
                .optional(),
        })
        .optional(),
});

export const ComponentPayloadValidator = ComponentBase.extend({
    id: ComponentValidator.shape.id.optional(),

    manufacturer: ManufacturerPayloadSchema.shape.id.nullable().default(null),
    teamManufacturer: z.string().nullable().default(null),
});

export { COMPONENT_IMAGE_TYPE_OPTIONS };
